import { AnimatePresence } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { OPEN_VIBECHAT_EVENT, OpenVibeChatEventDetail } from "@/components/VibeChat/constants";
import { DATA_ATTR_PROP_FOLLOW_MODE_AREA } from "@/components/VibeChat/hooks/useFollowMode";
import { chatStoreInstance } from "@/components/VibeChat/hooks/useVibeChat";
import { FloatingFab } from "@/components/VibeChat/widgets/FloatingWidget/FloatingFab";
import { FloatingPanel } from "@/components/VibeChat/widgets/FloatingWidget/FloatingPanel";
import { usePanelPosition } from "@/components/VibeChat/widgets/FloatingWidget/hooks/usePanelPosition";
import { useVibeChatNotifications } from "@/components/VibeChat/widgets/FloatingWidget/hooks/useVibeChatNotifications";
import { ScreenGlowOverlay } from "@/components/VibeChat/widgets/FloatingWidget/ScreenGlowOverlay";
import { AUTH_CONFIG } from "@/constants";
import useLocalStorageState, { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";

const FollowModeGlow = observer(() => (
  <AnimatePresence>
    {chatStoreInstance.isFollowMode && chatStoreInstance.isAnyLoading && (
      <ScreenGlowOverlay key="glow" />
    )}
  </AnimatePresence>
));

export const FloatingWidget = observer(() => {
  const { pathname } = useLocation();
  const [openOnMount, setOpenOnMount] = useLocalStorageState<boolean>(
    LOCAL_STORAGE_KEYS.OPEN_VIBECHAT_ON_MOUNT,
    false,
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [shouldPendingPromptStartNewChat, setShouldPendingPromptStartNewChat] = useState(false);
  const [panelPosition, setPanelPosition] = usePanelPosition();
  const previousPathnameRef = useRef(pathname);
  const isFullscreenRef = useRef(isFullscreen);

  isFullscreenRef.current = isFullscreen;

  useVibeChatNotifications(isExpanded);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const handleToggleSize = useCallback(() => {
    if (isFullscreen) {
      setIsFullscreen(false);
      setIsExpanded(true);
      return;
    }
    setIsFullscreen(true);
    setIsExpanded(true);
  }, [isFullscreen]);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const { detail } = event as CustomEvent<OpenVibeChatEventDetail>;
      const { prompt, newChat } = detail ?? {};
      if (prompt) {
        setPendingPrompt(prompt);
        setShouldPendingPromptStartNewChat(Boolean(newChat));
      }
      setIsExpanded(true);
    };
    window.addEventListener(OPEN_VIBECHAT_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_VIBECHAT_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    if (openOnMount) {
      setIsExpanded(true);
      setOpenOnMount(false);
    }
  }, [openOnMount, setOpenOnMount]);

  useEffect(() => {
    chatStoreInstance.setFollowModeDisabled(isFullscreen);
  }, [isFullscreen]);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return;
    }

    previousPathnameRef.current = pathname;

    if (!isFullscreenRef.current) {
      return;
    }

    setIsFullscreen(false);
  }, [pathname]);

  if (AUTH_CONFIG.isOnPrem) {
    return null;
  }

  return (
    <>
      <FollowModeGlow />

      <div {...DATA_ATTR_PROP_FOLLOW_MODE_AREA}>
        <AnimatePresence
          mode="popLayout"
          onExitComplete={() => {
            setIsFullscreen(false);
          }}
        >
          {isExpanded ? (
            <FloatingPanel
              key="panel"
              isFullscreen={isFullscreen}
              panelPosition={panelPosition}
              setPanelPosition={setPanelPosition}
              pendingPrompt={pendingPrompt}
              shouldPendingPromptStartNewChat={shouldPendingPromptStartNewChat}
              onPendingPromptConsumed={() => {
                setPendingPrompt(null);
                setShouldPendingPromptStartNewChat(false);
              }}
              onClose={handleClose}
              onToggleSize={handleToggleSize}
            />
          ) : (
            !isFullscreen && (
              <FloatingFab
                key="fab"
                panelPosition={panelPosition}
                setPanelPosition={setPanelPosition}
                onClick={() => setIsExpanded(true)}
                isActive={chatStoreInstance.isAnyLoading}
              />
            )
          )}
        </AnimatePresence>
      </div>
    </>
  );
});
